import { Link, Navigate, useLocation } from 'react-router-dom';
import useFetch, { FetchConfig, Status } from '../../../hooks/fetch.hook';
import type { UserApiModel } from '../models/user.api-model';
import { CompetitionApiModel } from '../models/competition.api-model';
import type ApplicationFormApiModel from '../models/participation.api-model';
import ParticipationApiModel from '../models/participation.api-model';
import type IParticipation from '../../../models/pa-egna-ben/participation.model';
import Spinner from '../../Shared/Spinner';
import GeneralError from '../../Shared/GeneralError';
import { ParticipationHelper } from '../helpers/participation.helper';
import { SystemClock } from '../services/system-clock.service';
import LinkListWithHeading from '../Shared/LinkListWithHeading';
import PageIntroduction from '../Shared/PageIntroduction';
import InformationBox from '../Shared/InformationBox';
import { ConfigurationService } from '../../../services/configuration.service';
import './style.scss';
import { InformationBoxType } from '../Shared/InformationBox/information-box-type.enum';

function processReceivedData(data: ApplicationFormApiModel[]): IParticipation[] {
  return data?.map((d) => ParticipationApiModel.mapToBusinessModel(d)) ?? [];
}

function Teacher() {
  // === STATE ===
  const [{ status: currentUserStatus, data: currentUserData }] = useFetch<UserApiModel[]>('/api/paegnaben/teacher/current', false, false, FetchConfig.default);
  const [{ status: getCurrentCompetitionStatus, data: getCurrentCompetitionData }] = useFetch<CompetitionApiModel>('/api/paegnaben/teacher/competitions/current');
  const [{ status: participationsStatus, data: participationsData, httpResponseStatusCode: participationsHttpResponseStatusCode }] = useFetch<ParticipationApiModel[]>(
    '/api/paegnaben/teacher/participations',
    currentUserStatus !== Status.success
  );

  // === HOOKS ===
  const location = useLocation();

  // === VARIABLES ===
  const loadingDataInProgress = currentUserStatus === Status.loading || participationsStatus === Status.loading;
  const participations = participationsData ? processReceivedData(participationsData) : undefined;
  const currentCompetition = getCurrentCompetitionData ? CompetitionApiModel.mapToBusinessModel(getCurrentCompetitionData) : undefined;
  const isRegistrationTimeEndTimePassed = currentCompetition?.registrationEndTime ? SystemClock.now() > currentCompetition?.registrationEndTime : undefined;
  const isRegistrationStartTimePassed = currentCompetition?.registrationStartTime ? SystemClock.now() > currentCompetition?.registrationStartTime : undefined;
  const participantRegistrationId = new URLSearchParams(location.search).get('id');
  const participantRegistration = participations?.find((p) => p.id.toString() === participantRegistrationId);

  // === RENDER ===
  if (currentUserStatus === Status.failed && participationsHttpResponseStatusCode === 401) {
    return <Navigate to="/session-utgangen" />;
  }
  if (currentUserStatus === Status.failed && (participationsHttpResponseStatusCode === 403 || !participationsHttpResponseStatusCode)) {
    return <Navigate to="/atkomst-saknas" />;
  }

  function getLatestReportingDateForCurrentLeg(participation: IParticipation): string | undefined {
    const currentLeg = ParticipationHelper.getCurrentLeg(participation, SystemClock.now());

    if (!currentLeg || participation.legReports.find((legReport) => legReport.legId === currentLeg.id)) {
      return undefined;
    }

    return `Rapportera senast ${currentLeg.endTime.toLocaleString({ month: 'long', day: 'numeric' })}`;
  }

  return (
    <div className="teacher">
      {loadingDataInProgress && <Spinner className="my-4 my-md-5" />}
      {currentUserData && !loadingDataInProgress && (
        <div>
          <PageIntroduction heading="Mina sidor" />
          {participantRegistrationId && participantRegistration?.school && (
            <InformationBox
              type={InformationBoxType.Success}
              headingElement={
                <h2 className="h4 mb-0">
                  Välkommen till utmaningen {participantRegistration?.name} {participantRegistration?.school.name}
                </h2>
              }
              textElement={
                <div>
                  <p>Startmaterial får du skickat till dig i god tid innan utmaningen startar. Det går också att ladda ner och skriva ut.</p>
                  <p>
                    <a href={ConfigurationService.get('paegnaben.teacherpage.startmaterialpagelink')}>Startmaterial för utmaningen</a>
                  </p>
                  <p className="mb-0">Lycka till i poängjakten!</p>
                </div>
              }
              className="mb-4 mb-sm-5"
              setFocus={true}
            />
          )}

          {!loadingDataInProgress && participationsStatus === Status.failed && currentCompetition && <GeneralError errorText="Kunde inte hämta ansökningar" className="mb-4" />}
          {!loadingDataInProgress && participationsStatus === Status.success && participations && participations.length > 0 && (
            <LinkListWithHeading
              heading={'Anmälda klasser'}
              listItems={participations.map((item) => (
                <Link key={item.id} to={`/klasser/${item.id}`} className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center text-decoration-none">
                  <span className="registered-participation-list-text-width">{`${item.name} ${item.school?.name}`}</span>
                  <p className="mb-0 pe-4">{getLatestReportingDateForCurrentLeg(item)}</p>
                </Link>
              ))}
            />
          )}
          {participations && participations.length === 0 && getCurrentCompetitionStatus && (
            <div className="mt-4 mt-md-5">
              <h2 className="h3">Inga deltagande klasser</h2>
              <p className="mb-0">
                {isRegistrationTimeEndTimePassed || !isRegistrationStartTimePassed ? 'Anmälan är stängd.' : `Anmälan är öppen till och med ${currentCompetition?.registrationEndTime.toISODate()}`}
              </p>
            </div>
          )}

          {!isRegistrationTimeEndTimePassed && isRegistrationStartTimePassed && (
            <div className="d-grid gap-2 d-sm-block">
              <Link to={'/anmalning'} className="btn btn-secondary mt-4">
                Anmäl klass
              </Link>
            </div>
          )}

          <LinkListWithHeading
            heading="Hantera konto"
            listItems={[
              <Link key="mina-uppgifter" to={'/mina-uppgifter'} className="text-decoration-none">
                <span>Mina uppgifter</span>
              </Link>,
              <form key={'logga-ut'} action="/konto/logga-ut" method="post">
                <button type="submit" className="btn--as-link text-decoration-none w-100 text-start">
                  <span className="">{'Logga ut'}</span>
                </button>
              </form>,
            ]}
            className="mt-4 mt-sm-5"
          />
        </div>
      )}
    </div>
  );
}

export default Teacher;
